/* eslint-disable no-underscore-dangle */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFeedback, updateFeedbackStatus, deleteFeedback } from '../../services/adminApi';
import './admin.css';

const TYPE_ICONS = {
  bug: { icon: '🐛', label: 'Bug Report' },
  feature: { icon: '✨', label: 'Feature Request' },
  general: { icon: '💬', label: 'General' },
  other: { icon: '📝', label: 'Other' },
};

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-800',
  reviewed: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
};

function FeedbackManager() {
  const [feedback, setFeedback] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const loadFeedback = async () => {
    try {
      setIsLoading(true);
      const data = await getFeedback();
      setFeedback(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, []);

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await updateFeedbackStatus(feedbackId, newStatus);
      setFeedback((prev) => prev.map((f) => (
        f._id === feedbackId ? { ...f, status: newStatus } : f
      )));
      if (selectedFeedback && selectedFeedback._id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (feedbackId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;

    try {
      await deleteFeedback(feedbackId);
      setFeedback((prev) => prev.filter((f) => f._id !== feedbackId));
      if (selectedFeedback && selectedFeedback._id === feedbackId) {
        setSelectedFeedback(null);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (message.length <= maxLength) return message;
    return `${message.substring(0, maxLength)}...`;
  };

  const filteredFeedback = filter === 'all'
    ? feedback
    : feedback.filter((f) => f.status === filter);

  const counts = {
    all: feedback.length,
    new: feedback.filter((f) => f.status === 'new').length,
    reviewed: feedback.filter((f) => f.status === 'reviewed').length,
    resolved: feedback.filter((f) => f.status === 'resolved').length,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-puzzle-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-puzzle-text">Feedback Manager</h1>
          <div className="flex gap-4">
            <Link to="/admin/stats" className="text-puzzle-blue hover:underline">
              Analytics
            </Link>
            <Link to="/admin/daily-puzzles" className="text-puzzle-blue hover:underline">
              Daily Puzzles
            </Link>
            <Link to="/admin/images" className="text-puzzle-blue hover:underline">
              Image Library
            </Link>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-4 p-4 bg-puzzle-danger/10 border border-puzzle-danger/30 rounded-lg flex justify-between items-center">
            <p className="text-puzzle-danger">{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-puzzle-danger hover:opacity-80"
            >
              ✕
            </button>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['all', 'new', 'reviewed', 'resolved'].map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === status
                  ? 'bg-puzzle-blue text-white'
                  : 'bg-puzzle-surface text-puzzle-text hover:bg-puzzle-hover'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {' '}
              <span className="text-sm opacity-75">
                (
                {counts[status]}
                )
              </span>
            </button>
          ))}
        </div>

        {/* Feedback Table */}
        <div className="bg-puzzle-surface rounded-lg shadow overflow-hidden">
          {filteredFeedback.length === 0 ? (
            <div className="p-8 text-center text-puzzle-text-secondary">
              No feedback found
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-puzzle-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-puzzle-text-secondary">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-puzzle-text-secondary">Message</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-puzzle-text-secondary">User</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-puzzle-text-secondary">Page</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-puzzle-text-secondary">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-puzzle-text-secondary">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-puzzle-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-puzzle-border">
                {filteredFeedback.map((f) => (
                  <tr
                    key={f._id}
                    className={f.status === 'new' ? 'bg-puzzle-blue/10' : ''}
                  >
                    <td className="px-4 py-3">
                      <span title={TYPE_ICONS[f.type]?.label || f.type}>
                        {TYPE_ICONS[f.type]?.icon || '📝'}
                      </span>
                      {f.screenshot && (
                        <span className="ml-1" title="Has screenshot">📷</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-puzzle-text">
                      {truncateMessage(f.message)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {f.userId ? (
                        <span className="text-puzzle-text">{f.userId.username}</span>
                      ) : (
                        <span className="text-puzzle-text-secondary">Guest</span>
                      )}
                      {f.email && (
                        <div className="text-xs text-puzzle-text-secondary">{f.email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-puzzle-text-secondary">
                      {f.pageUrl?.replace(/^https?:\/\/[^/]+/, '') || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-puzzle-text-secondary">
                      {formatDate(f.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[f.status]}`}>
                        {f.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedFeedback(f)}
                          className="text-puzzle-blue hover:underline text-sm"
                        >
                          View
                        </button>
                        {f.status === 'new' && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(f._id, 'reviewed')}
                            className="text-yellow-600 hover:underline text-sm"
                          >
                            Mark Reviewed
                          </button>
                        )}
                        {f.status === 'reviewed' && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(f._id, 'resolved')}
                            className="text-green-600 hover:underline text-sm"
                          >
                            Resolve
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDelete(f._id)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail Modal */}
        {selectedFeedback && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-puzzle-surface rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold text-puzzle-text">
                  {TYPE_ICONS[selectedFeedback.type]?.icon}
                  {' '}
                  {TYPE_ICONS[selectedFeedback.type]?.label || 'Feedback'}
                </h2>
                <button
                  type="button"
                  onClick={() => setSelectedFeedback(null)}
                  className="text-puzzle-text-secondary hover:text-puzzle-text"
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                {/* Status */}
                <div>
                  <p className="text-sm text-puzzle-text-secondary mb-1">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selectedFeedback.status]}`}>
                    {selectedFeedback.status}
                  </span>
                </div>

                {/* Message */}
                <div>
                  <p className="text-sm text-puzzle-text-secondary mb-1">Message</p>
                  <p className="text-puzzle-text whitespace-pre-wrap bg-puzzle-muted p-3 rounded-lg">
                    {selectedFeedback.message}
                  </p>
                </div>

                {/* Screenshot */}
                {selectedFeedback.screenshot && (
                  <div>
                    <p className="text-sm text-puzzle-text-secondary mb-1">Screenshot</p>
                    <button
                      type="button"
                      onClick={() => {
                        const win = window.open('', '_blank');
                        win.document.write(`
                          <html>
                            <head><title>Feedback Screenshot</title></head>
                            <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#1a1a1a;">
                              <img src="${selectedFeedback.screenshot}" style="max-width:100%;max-height:100vh;" />
                            </body>
                          </html>
                        `);
                        win.document.close();
                      }}
                      className="block text-left"
                    >
                      <img
                        src={selectedFeedback.screenshot}
                        alt="Feedback screenshot"
                        className="max-w-full max-h-64 rounded-lg border border-puzzle-border hover:opacity-90 transition-opacity cursor-pointer"
                      />
                      <p className="text-xs text-puzzle-blue mt-1">Click to view full size</p>
                    </button>
                  </div>
                )}

                {/* User Info */}
                <div>
                  <p className="text-sm text-puzzle-text-secondary mb-1">User</p>
                  <p className="text-puzzle-text">
                    {selectedFeedback.userId ? selectedFeedback.userId.username : 'Guest'}
                    {selectedFeedback.userId?.email && (
                      <span className="text-puzzle-text-secondary ml-2">
                        (
                        {selectedFeedback.userId.email}
                        )
                      </span>
                    )}
                  </p>
                  {selectedFeedback.email && !selectedFeedback.userId && (
                    <p className="text-sm text-puzzle-text-secondary">
                      Contact:
                      {' '}
                      {selectedFeedback.email}
                    </p>
                  )}
                </div>

                {/* Page URL */}
                <div>
                  <p className="text-sm text-puzzle-text-secondary mb-1">Page</p>
                  <p className="text-puzzle-text text-sm font-mono bg-puzzle-muted p-2 rounded">
                    {selectedFeedback.pageUrl}
                  </p>
                </div>

                {/* User Agent */}
                {selectedFeedback.userAgent && (
                  <div>
                    <p className="text-sm text-puzzle-text-secondary mb-1">Browser/Device</p>
                    <p className="text-puzzle-text-secondary text-xs font-mono bg-puzzle-muted p-2 rounded break-all">
                      {selectedFeedback.userAgent}
                    </p>
                  </div>
                )}

                {/* Date */}
                <div>
                  <p className="text-sm text-puzzle-text-secondary mb-1">Submitted</p>
                  <p className="text-puzzle-text">{formatDate(selectedFeedback.createdAt)}</p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-2 p-4 border-t">
                {selectedFeedback.status === 'new' && (
                  <button
                    type="button"
                    onClick={() => handleStatusChange(selectedFeedback._id, 'reviewed')}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                  >
                    Mark as Reviewed
                  </button>
                )}
                {selectedFeedback.status === 'reviewed' && (
                  <button
                    type="button"
                    onClick={() => handleStatusChange(selectedFeedback._id, 'resolved')}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                  >
                    Mark as Resolved
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(selectedFeedback._id)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedFeedback(null)}
                  className="px-4 py-2 bg-puzzle-muted text-puzzle-text rounded-lg hover:bg-puzzle-hover ml-auto"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FeedbackManager;
